import React, { useState, forwardRef, useImperativeHandle, useEffect } from "react";
import AddInputs from "../../../../components/Add/AddInputs";
import { useDispatch, useSelector } from "react-redux";
import {
    setServiceFeeType,
    setUpfrontServiceFee,
    setPostpaidServiceFeeDetails,
    addPostpaidServiceFeeDetail,
    updatePostpaidServiceFeeDetail,
    removePostpaidServiceFeeDetail,
    setAddLoading,
    setSavedData,
    setServiceFeeErrors,
    clearServiceFeeErrors,
    setMNOList,
    setMNOLoading,
    setMNOError,
    selectMNO,
    removeMNO,
    clearSelectedMNOs,
    setServiceFeeParent,
    setChildUpfrontServiceFee,
    setChildPostpaidServiceFeeDetails,
    setCompleteServiceFee
} from "../addMDSlice";
import { RootState } from "../../../../store";
import Image from "../../../../components/images/Image";
import useApi from "../../../../services/useApi";
import CustomModal from "../../../../components/CustomModal";
import TermsAndConditionsModal from "../../../../components/CustomModal/TermsAndConditionsModal";
import { useNavigate } from "react-router-dom";
import { showToast } from "../../../../components/toastMessage/toastSlice";
import { clearSignaturePath } from "../../../../store/slices/signatureSlice";
import NestedDropdown from "../../../../components/Dropdowns/NestedDropdown";
import InputLabel from "../../../../components/inputfields/InputLabel";
import ErrorMessage from "../../../../components/ErrorMessages/ErrorMessage";
import CustomRadioButton from "../../../../components/buttons/CustomRadioButton";
import NumberInputField from "../../../../components/inputfields/NumberInputfield";
import AdjustChildServiceFeeInput from "../../../../components/inputfields/AdjustChildServiceFeeInput";
import { setServiceFee } from "../../../MNOManagament/OnTargetAchievement/OnTargetAchievementSlice";
import PostpaidServiceFeeFields from "./PostpaidServiceFeeFields";
import BasicDetails from "./BasicDetails";
import { L } from "vitest/dist/chunks/reporters.nr4dxCkA.js";
import { ServiceFeePostpaid, ServiceFeeUpfront } from "./types";

interface ServiceFeeProps {
    id: string;
    type: string;
    isTemporaryUser?: boolean;
    handleNext?: () => void;
    setAddCvmClicked?: (value: boolean) => void;
    setEditLoading?: (value: boolean) => void;
    onHasChanges?: (hasChanges: boolean) => void;
    onEditModalChange?: (isOpen: boolean) => void;
}

export interface serviceFeeRef {
    handleSubmit: () => void;
}

interface MNOOption {
    id: string;
    name: string;
    etopup_dispersive_fee_percent: string;
    bundle_dispersive_fee_percent: string;
}

interface PostpaidDetail {
    mno_id: string;
    mno_name: string;
    bundle_dispersive_fee_percent: number | string;
    etopup_dispersive_fee_percent: number | string;
    child_bundle_dispersive_fee_percent?: number | string;
    child_etopup_dispersive_fee_percent?: number | string;
    status: boolean;
}

interface ChildPostpaidDetail {
    mno_id: string;
    mno_name: string;
    child_bundle_dispersive_fee_percent: number | string;
    child_etopup_dispersive_fee_percent: number | string;
    status: boolean;
}

interface LocalServiceFeeState {
    service_fee_type: string;
    service_fee_parent: string;
    service_fee_parent_id?: string;
    upfront_service_fee_percentage: number | string;
    service_fee_details: PostpaidDetail[];
}

// Modify the TYPE_MAPPINGS to include endpoint mapping for each option
const TYPE_MAPPINGS = {
    "Master Distributors": {
        parentOptions: ["ZoodPay"],
        apiEndpoint: "master-distributors"
    },
    "Distributors": {
        parentOptions: ["ZoodPay", "Master Distributor"],
        apiEndpoint: "distributors"
    },
    "Sub Distributors": {
        parentOptions: ["ZoodPay", "Master Distributor", "Distributor"],
        apiEndpoint: "sub-distributors"
    },
    "Agents": {
        parentOptions: ["ZoodPay", "Master Distributor", "Distributor", "Sub Distributor"],
        apiEndpoint: "agents"
    },
    "Retailers": {
        parentOptions: ["ZoodPay", "Master Distributor", "Distributor", "Sub Distributor", "Agent"],
        apiEndpoint: "retailers"
    }
};

// Add option to endpoint mapping
const OPTION_TO_ENDPOINT = {
    "Master Distributor": "master-distributors",
    "Distributor": "distributors",
    "Sub Distributor": "sub-distributors",
    "Agent": "agents",
    "Retailer": "retailers"
};

const ServiceFee = forwardRef<serviceFeeRef, ServiceFeeProps>(({ id, type, handleNext, setAddCvmClicked, setEditLoading, onHasChanges, onEditModalChange, isTemporaryUser }, ref) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { getAPI, PatchAPI } = useApi();
    const [localFormState, setLocalFormState] = useState<LocalServiceFeeState>({
        service_fee_type: "",
        service_fee_parent: "",
        upfront_service_fee_percentage: '',
        service_fee_details: []
    });

    // Local state for errors
    const [localErrors, setLocalErrors] = useState<{ [key: string]: string }>({});

    // MNO related state
    const [maxUpfrontServiceFeePercent, setMaxUpfrontServiceFeePercent] = useState(0);
    const [parentUpfrontServiceFee, setParentUpfrontServiceFee] = useState(0);
    const [parentServiceFeeType, setParentServiceFeeType] = useState("");
    const [parentServiceFeeDetails, setParentServiceFeeDetails] = useState<PostpaidDetail[]>([]);
    const [parentMDSelected, setParentMDSelected] = useState(false);
    const [childUpfrontFeePercentage, setChildUpfrontFeePercentage] = useState<string | number>(0);
    const [selectedParentType, setSelectedParentType] = useState("");
    const [childUpfrontError, setChildUpfrontError] = useState({
        child_upfront_service_fee_percentage: ""
    });
    const [childPostpaidPercentage, setChildPostpaidPercentage] = useState<{
        [key: number]: {
            child_bundle_dispersive_fee_percent: string | number;
            child_etopup_dispersive_fee_percent: string | number;
        };
    }>({});
    const [childPostpaidErrors, setChildPostpaidErrors] = useState<{
        [key: number]: {
            child_bundle_dispersive_fee_percent?: string;
            child_etopup_dispersive_fee_percent?: string;
        };
    }>({});


    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditModal, setIsEditModal] = useState(false);
    const [termsAndConditions, setTermsAndConditions] = useState(false);
    const [acceptButtonLoading, setAcceptButtonLoading] = useState(false);
    const [editModalLoading, setEditModalLoading] = useState(false);
    const [selectedOption, setSelectedOption] = useState<string>("Decrease");
    const [selectedOptionPercentage, setSelectedOptionPercentage] = useState<number | string | null>(0);

    // Get Redux state that we still need
    const service_fee = useSelector((state: RootState) => state.addMD.service_fee);
    const serviceFeeErrors = useSelector((state: RootState) => state.addMD.serviceFeeErrors);
    const { user_id, profile, add_loading } = useSelector((state: RootState) => state.addMD);

    const extractParentCode = (parent: string) => {
        if (parent === "ZoodPay") return "ZoodPay";

        // Extract the code from within parentheses
        const codeMatch = parent.match(/\(([^)]+)\)/);
        if (codeMatch) {
            const userCode = codeMatch[1];
            // Extract the prefix (letters before numbers) from the user code
            const prefixMatch = userCode.match(/^([A-Za-z]+)/);
            if (prefixMatch) {
                return prefixMatch[1]; // Return just the prefix (e.g., "MDST" from "MDST325126")
            }
        }
        return parent;
    };
    const userRoleMap: any = {
        'MDST': 'master-distributor',
        'DST': 'distributor',
        'SDST': 'sub-distributor',
        'AGT': 'agent',
        'RET': 'retailer',
    };
    // Initialize local state from Redux when the component mounts
    useEffect(() => {
        if (service_fee) {
            const baseState = {
                service_fee_type: service_fee.service_fee_type || "",
                service_fee_parent: service_fee.service_fee_parent === 'ZoodPay' ? service_fee.service_fee_parent : service_fee.service_fee_parent?.split(" ").length === 1 ? service_fee.service_fee_parent_name : service_fee.service_fee_parent || "",
                service_fee_parent_id: service_fee.service_fee_parent_id,
                upfront_service_fee_percentage: 0,
                service_fee_details: []
            };

            if (service_fee.service_fee_parent === "ZoodPay") {
                if ('upfront_service_fee_percentage' in service_fee) {
                    baseState.upfront_service_fee_percentage = (service_fee as ServiceFeeUpfront).upfront_service_fee_percentage || 0;
                }
                if ('service_fee_details' in service_fee) {
                    baseState.service_fee_details = (service_fee as ServiceFeePostpaid).service_fee_details || [];
                }
            }

            setLocalFormState(baseState);
            setEditLoading?.(true)

            // If there's a parent selected and it's not ZoodPay, set parent states
            if (service_fee.service_fee_parent && service_fee.service_fee_parent !== "ZoodPay" && type !== "Master Distributors") {
                setParentMDSelected(true);
                setParentServiceFeeType(service_fee.service_fee_type);
                const parentCode = extractParentCode(service_fee.service_fee_parent);
                setSelectedParentType(userRoleMap?.[parentCode])
                let responseData: any;
                const parentDetails = async () => {
                    responseData = service_fee?.service_fee_parent_id ? await fetchMDDetails(service_fee?.service_fee_parent_id, userRoleMap?.[parentCode]) : [];
                    if (responseData?.data?.service_fee) {
                        if (service_fee.service_fee_type === "Upfront") {
                            setLocalFormState(prev => ({
                                ...prev,
                                upfront_service_fee_percentage: responseData.data.service_fee.upfront_service_fee_percentage || 0
                            }));
                        }
                    }
                }

                if (service_fee.service_fee_type === "Upfront") {
                    const upfrontFee = (service_fee as ServiceFeeUpfront).child_upfront_service_fee_percentage || (service_fee as ServiceFeeUpfront).upfront_service_fee_percentage || "";
                    setChildUpfrontFeePercentage(upfrontFee);
                    parentDetails()
                } else if (service_fee.service_fee_type === "Postpaid") {
                    const fetchMNOsData = async () => {
                        const response = await fetchMDDetails(service_fee?.service_fee_parent_id, userRoleMap?.[parentCode]);
                        if (!response.error) {
                            const postpaidService = service_fee as ServiceFeePostpaid;
                            const updatedDetails = postpaidService.service_fee_details.map(detail => {
                                const mno = response?.data?.service_fee?.service_fee_details.find((m: any) => m.mno_id === detail.mno_id);

                                return {
                                    mno_id: detail.mno_id,
                                    mno_name: mno?.name || "",
                                    bundle_dispersive_fee_percent: mno?.bundle_dispersive_fee_percent || 0,
                                    etopup_dispersive_fee_percent: mno?.etopup_dispersive_fee_percent || 0,
                                    child_bundle_dispersive_fee_percent: detail.child_bundle_dispersive_fee_percent || detail.bundle_dispersive_fee_percent || 0,
                                    child_etopup_dispersive_fee_percent: detail.child_etopup_dispersive_fee_percent || detail.etopup_dispersive_fee_percent || 0,
                                    status: detail.status
                                };
                            });

                            setLocalFormState(prev => ({
                                ...prev,
                                service_fee_details: updatedDetails
                            }));
                        }
                    };

                    fetchMNOsData();

                    if ((service_fee as ServiceFeePostpaid).service_fee_details) {
                        (service_fee as ServiceFeePostpaid).service_fee_details.forEach((detail, index) => {
                            setChildPostpaidPercentage(prev => ({
                                ...prev,
                                [index]: {
                                    child_bundle_dispersive_fee_percent: detail.bundle_dispersive_fee_percent || "",
                                    child_etopup_dispersive_fee_percent: detail.etopup_dispersive_fee_percent || "",
                                }
                            }));
                        });
                    }
                }
            }
        }
    }, [service_fee, type]);
    // Initialize MNO list if needed
    useEffect(() => {
        if (service_fee?.service_fee_type === "Postpaid") {
            handleMaxUprontPercentage();
        } else if (service_fee?.service_fee_type === "Upfront") {
            handleMaxUprontPercentage();
        }
    }, [service_fee?.service_fee_type]);

    // Add effect to notify parent of edit modal state changes
    useEffect(() => {
        if (onEditModalChange) {
            onEditModalChange(isEditModal);
        }
    }, [isEditModal, onEditModalChange]);

    // Validation function
    const validateServiceFee = () => {
        const errors: { [key: string]: string } = {};
        const childErrors: {
            [key: number]: {
                child_bundle_dispersive_fee_percent?: string;
                child_etopup_dispersive_fee_percent?: string;
            };
        } = {};
        if (!localFormState.service_fee_type) {
            errors.service_fee_type = "This field is required";
        }
        if (localFormState.service_fee_parent === "" && type !== "Master Distributors") {
            errors.service_fee_parent = "This field is required";
        }

        if (localFormState.service_fee_type === "Upfront" && !parentMDSelected) {
            const upfrontPercentage = localFormState.upfront_service_fee_percentage;
            if (upfrontPercentage === 0 || upfrontPercentage === null || upfrontPercentage === "") {
                errors.upfront_service_fee_percentage = "This field is required";
            } else if (Number(upfrontPercentage) < 0.01) {
                errors.upfront_service_fee_percentage = "Percentage must be greater than or equal to 0.01";
            }
            if (Number(upfrontPercentage) > maxUpfrontServiceFeePercent && upfrontPercentage !== 0) {
                errors.upfront_service_fee_percentage = `Percentage must not exceed ${maxUpfrontServiceFeePercent.toFixed(2)}%`;
            }
        } else if (localFormState.service_fee_type === "Postpaid" && !parentMDSelected) {
            if (localFormState.service_fee_details.length === 0) {
                errors.general = "At least one postpaid detail is required";
            }

            localFormState.service_fee_details.forEach((detail, index) => {
                if (!detail.mno_id) {
                    errors[`postpaid_${index}_mno_id`] = "This field is required";
                }
                if (Number(detail.bundle_dispersive_fee_percent) < 0.01) {
                    errors[`postpaid_${index}_bundle`] = "Percentage must be greater than or equal to 0.01";
                }
                if (Number(detail.etopup_dispersive_fee_percent) < 0.01) {
                    errors[`postpaid_${index}_etopup`] = "Percentage must be greater than or equal to 0.01";
                }
                if (!detail.bundle_dispersive_fee_percent) {
                    errors[`postpaid_${index}_bundle`] = "This field is required";
                }
                if (!detail.etopup_dispersive_fee_percent) {
                    errors[`postpaid_${index}_etopup`] = "This field is required";
                }

                const selectedMno = mnoList.find(mno => mno.id === detail.mno_id);
                if (selectedMno) {
                    const maxBundlePercent = Number(selectedMno.bundle_dispersive_fee_percent);
                    const maxEtopupPercent = Number(selectedMno.etopup_dispersive_fee_percent);

                    if (Number(detail.bundle_dispersive_fee_percent) > maxBundlePercent) {
                        errors[`postpaid_${index}_bundle`] = `Percentage must not exceed ${maxBundlePercent.toFixed(2)}%`;
                    }
                    if (Number(detail.etopup_dispersive_fee_percent) > maxEtopupPercent) {
                        errors[`postpaid_${index}_etopup`] = `Percentage must not exceed ${maxEtopupPercent.toFixed(2)}%`;
                    }
                }
            });
        } else if (localFormState.service_fee_type === "Postpaid" && parentMDSelected) {
            localFormState.service_fee_details.forEach((detail, index) => {
                const childPercentages = detail || {};

                if (!childErrors[index]) {
                    childErrors[index] = {};
                }

                if (!childPercentages.child_bundle_dispersive_fee_percent) {
                    childErrors[index].child_bundle_dispersive_fee_percent = "This field is required";
                } else if (Number(childPercentages.child_bundle_dispersive_fee_percent) < 0.01) {
                    childErrors[index].child_bundle_dispersive_fee_percent = "Percentage must be greater than or equal to 0.01";
                } else if (Number(childPercentages.child_bundle_dispersive_fee_percent) > Number(detail.bundle_dispersive_fee_percent)) {
                    childErrors[index].child_bundle_dispersive_fee_percent = `Percentage must not exceed ${detail.bundle_dispersive_fee_percent}%`;
                }

                if (!childPercentages.child_etopup_dispersive_fee_percent) {
                    childErrors[index].child_etopup_dispersive_fee_percent = "This field is required";
                } else if (Number(childPercentages.child_etopup_dispersive_fee_percent) < 0.01) {
                    childErrors[index].child_etopup_dispersive_fee_percent = "Percentage must be greater than or equal to 0.01";
                } else if (Number(childPercentages.child_etopup_dispersive_fee_percent) > Number(detail.etopup_dispersive_fee_percent)) {
                    childErrors[index].child_etopup_dispersive_fee_percent = `Percentage must not exceed ${detail.etopup_dispersive_fee_percent}%`;
                }
            });
        }
        setChildPostpaidErrors(childErrors);
        return {
            errors,
            childErrors,
            hasChildErrors: Object.keys(childErrors).length > 0 &&
                Object.values(childErrors).some(error => Object.keys(error as object).length > 0)
        };
    };
    const fetchMasterDistributors = async (endpoint: string, searchTerm: string, page: number = 1) => {
        try {
            // Get the endpoint from the selected option
            setSelectedParentType(endpoint);
            const selectedOption = endpoint.split('/').pop()?.replace(/-/g, ' ');
            const apiEndpoint = OPTION_TO_ENDPOINT[selectedOption as keyof typeof OPTION_TO_ENDPOINT] || "master-distributors";

            const response = await getAPI(`${endpoint + 's'}/?page_number=${page}${searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : ""}&assign_fee_mode=true`);
            if (!response.error) {
                return {
                    data: {
                        users: response.data.data.map((md: any) => ({
                            id: md.id,
                            user_name: md.first_name + ' ' + (md.last_name ? md.last_name : ''),
                            user_code: md.user_code,
                            status: md.service_fee_type || "N/A"
                        })),
                        total_count: response.data.total_count
                    }
                };
            }
            return { data: { users: [], total_count: 0 } };
        } catch (error) {
            console.error("Error fetching users:", error);
            return { data: { users: [], total_count: 0 } };
        }
    };
    const fetchMDDetails = async (mdId: string, parentType?: string) => {
        try {
            // Get the endpoint from the selected parent
            const selectedParent = localFormState.service_fee_parent;
            const apiEndpoint = OPTION_TO_ENDPOINT[selectedParent as keyof typeof OPTION_TO_ENDPOINT] || "master-distributors";

            const response = await getAPI(`${(selectedParentType || parentType) + 's'}/${mdId}?assign_fee_mode=true`);
            if (!response.error) {
                return response.data;
            }
            return null;
        } catch (error) {
            console.error("Error fetching user details:", error);
            return null;
        }
    };
    const handleMDSelection = async (userData: any, action: boolean) => {
        if (userData && userData.id) {
            const mdDetails = await fetchMDDetails(userData.id);

            if (mdDetails) {
                const serviceType = mdDetails.data.service_fee.service_fee_type;

                // Set parent service fee type first
                setParentServiceFeeType(serviceType);
                setParentMDSelected(true);

                // Prepare service fee data based on type
                let feeData = {};
                if (serviceType === 'Upfront') {
                    setParentUpfrontServiceFee(mdDetails.data.service_fee.upfront_service_fee_percentage);
                    feeData = {
                        upfront_service_fee_percentage: mdDetails.data.service_fee.upfront_service_fee_percentage,
                        service_fee_details: [] // Clear any existing postpaid details
                    };
                } else if (serviceType === 'Postpaid') {
                    const details = mdDetails.data.service_fee.service_fee_details || [];
                    setParentServiceFeeDetails(details);
                    feeData = {
                        upfront_service_fee_percentage: 0, // Clear any existing upfront percentage
                        service_fee_details: details
                    };
                }

                // Update the state in one batch with all needed info
                const updatedState = {
                    service_fee_parent: userData.user_name + ` (${userData.user_code})`, // Store code for API
                    service_fee_parent_name: userData.user_name + `(${userData.user_code})`, // Store name for display
                    service_fee_parent_id: userData.id,
                    service_fee_type: serviceType,
                    ...feeData
                };

                setLocalFormState(prevState => ({
                    ...prevState,
                    ...updatedState
                }));

                // Clear any errors related to parent
                const newErrors = { ...localErrors };
                delete newErrors.service_fee_parent;
                setLocalErrors(newErrors);
            }
        }
    };

    // Helper function to format percentage
    const formatPercentage = (value: number | string): number => {
        const num = Number(value);
        if (isNaN(num)) return 0;
        // If number has no decimal places, add .00
        return Number(num.toFixed(2));
    };

    const handleSubmit = async () => {
        if (id === '' || type === 'Retailers') {
            const isValid = handleValidateAllFields();
            if (!isValid) {
                dispatch(setAddLoading(false));
                return;
            }
        }
        const getUrlPath = (type: string) => type?.toLowerCase().split(' ').join('-');
        let serviceFeeData = { ...localFormState };

        // Extract MDST code or keep ZoodPay

        let payloadServiceFeeData = { ...serviceFeeData };
        if (payloadServiceFeeData.service_fee_parent) {
            payloadServiceFeeData.service_fee_parent = extractParentCode(payloadServiceFeeData.service_fee_parent);
        }

        if (type === "Master Distributors") {
            delete payloadServiceFeeData.service_fee_parent;
            delete payloadServiceFeeData.service_fee_parent_id;
            delete payloadServiceFeeData.service_fee_parent_name;
        } else if (payloadServiceFeeData.service_fee_parent === "ZoodPay") {
            delete payloadServiceFeeData.service_fee_parent_id;
        }

        // Create a copy for Redux state with all data
        const reduxServiceFeeData = { ...payloadServiceFeeData };

        if (payloadServiceFeeData.service_fee_type === "Upfront") {
            if (parentMDSelected) {
                // For API payload, use child percentage as main percentage
                payloadServiceFeeData.upfront_service_fee_percentage = formatPercentage(childUpfrontFeePercentage);
                // For Redux, store both parent and child percentages
                reduxServiceFeeData.upfront_service_fee_percentage = formatPercentage(localFormState.upfront_service_fee_percentage);
                reduxServiceFeeData.child_upfront_service_fee_percentage = formatPercentage(childUpfrontFeePercentage);
            } else {
                payloadServiceFeeData.upfront_service_fee_percentage = formatPercentage(localFormState.upfront_service_fee_percentage);
                reduxServiceFeeData.upfront_service_fee_percentage = formatPercentage(localFormState.upfront_service_fee_percentage);
            }
            delete payloadServiceFeeData.service_fee_details;
            delete reduxServiceFeeData.service_fee_details;
        } else if (payloadServiceFeeData.service_fee_type === "Postpaid") {
            delete payloadServiceFeeData.upfront_service_fee_percentage;
            delete reduxServiceFeeData.upfront_service_fee_percentage;

            if (parentMDSelected) {
                // For API payload, use child percentages as main percentages
                payloadServiceFeeData.service_fee_details = payloadServiceFeeData.service_fee_details.map((detail) => ({
                    mno_id: detail.mno_id,
                    bundle_dispersive_fee_percent: formatPercentage(detail.child_bundle_dispersive_fee_percent || ""),
                    etopup_dispersive_fee_percent: formatPercentage(detail.child_etopup_dispersive_fee_percent || ""),
                    status: detail.status
                }));

                // For Redux, store both parent and child percentages in a single array
                reduxServiceFeeData.service_fee_details = localFormState.service_fee_details.map(detail => ({
                    ...detail,
                    bundle_dispersive_fee_percent: formatPercentage(detail.bundle_dispersive_fee_percent),
                    etopup_dispersive_fee_percent: formatPercentage(detail.etopup_dispersive_fee_percent),
                    child_bundle_dispersive_fee_percent: formatPercentage(detail.child_bundle_dispersive_fee_percent || ""),
                    child_etopup_dispersive_fee_percent: formatPercentage(detail.child_etopup_dispersive_fee_percent || "")
                }));
            } else {
                payloadServiceFeeData.service_fee_details = payloadServiceFeeData.service_fee_details.map((detail) => ({
                    mno_id: detail.mno_id,
                    bundle_dispersive_fee_percent: formatPercentage(detail.bundle_dispersive_fee_percent),
                    etopup_dispersive_fee_percent: formatPercentage(detail.etopup_dispersive_fee_percent),
                    status: detail.status
                }));

                reduxServiceFeeData.service_fee_details = localFormState.service_fee_details.map(detail => ({
                    ...detail,
                    bundle_dispersive_fee_percent: formatPercentage(detail.bundle_dispersive_fee_percent),
                    etopup_dispersive_fee_percent: formatPercentage(detail.etopup_dispersive_fee_percent)
                }));
            }

            // Remove mno_name and updated_at from service_fee_details in payload
            payloadServiceFeeData.service_fee_details = payloadServiceFeeData.service_fee_details.map(({ mno_name, updated_at, child_bundle_dispersive_fee_percent, child_etopup_dispersive_fee_percent, ...rest }) => rest);
        }

        let payload = {
            service_fee: payloadServiceFeeData
        };
        // Add adjust_child_service_fee to payload if it exists
        if (id !== '' && type !== 'Retailers') {
            if (selectedOptionPercentage !== 0) {
                payload.service_fee.adjust_child_service_fee = selectedOption === "Increase" ? Number(selectedOptionPercentage) : -Number(selectedOptionPercentage);
            } else {
                payload.service_fee.adjust_child_service_fee = 0;
            }
        }

        // Set loading state based on whether we're in edit modal or not
        if (isEditModal) {
            setEditModalLoading(true);
        } else {
            dispatch(setAddLoading(true));
        }

        try {
            const response = await PatchAPI(`${getUrlPath(type)}/${user_id}`, payload);

            if (!response.error) {
                dispatch(clearServiceFeeErrors());
                setLocalErrors({});
                handleNext?.();
                setIsModalOpen(id ? false : true);
                setSelectedOptionPercentage(0);
                setSelectedOption('Decrease')
                setIsEditModal(false)
                if (profile === 'Pending' && id !== '') {
                    setTermsAndConditions(true);
                } else if (profile !== 'Pending' && id !== '') {
                    setTermsAndConditions(false)
                    navigate(`/user-management/${type
                        ?.toLowerCase()
                        .replace(/ /g, "-")
                        .slice(0, -1)}-management`);
                    dispatch(showToast({ message: "Details updated successfully", type: "success" }));
                } else if (id === ''){
                    dispatch(setSavedData({ key: 'service fee', value: reduxServiceFeeData }));
                    dispatch(setCompleteServiceFee(reduxServiceFeeData));
                }
                
            } else {
                const errorMsg = response?.data?.response?.data?.message || 'Something went wrong';
                setLocalErrors({ general: errorMsg });
                dispatch(setServiceFeeErrors({ general: errorMsg }));
                dispatch(showToast({
                    type: "error",
                    message: errorMsg
                }))
            }
        } catch (error) {
            console.error('API Error:', error);
            setLocalErrors({ general: 'An error occurred while saving service fee details' });
            dispatch(setServiceFeeErrors({
                general: 'An error occurred while saving service fee details'
            }));

        } finally {
            if (isEditModal) {
                setEditModalLoading(false);
            } else {
                dispatch(setAddLoading(false));
            }
        }
    };

    const handleMaxUprontPercentage = async () => {
        try {
            let response = await getAPI(`mnos/?max_upfront_fee=true`);
            setMnoList(response?.data.data || []);
            setMaxUpfrontServiceFeePercent(response?.data.max_upfront_service_fee_percent || 0);

            // Also update Redux for compatibility with existing code
            dispatch(setMNOList({
                data: response?.data.data || [],
                maxUpfrontServiceFeePercent: response?.data.max_upfront_service_fee_percent || 0
            }));
        } catch (error) {
            console.error('Error fetching max upfront percentage:', error);
        }
    }

    const handleTermsAndConditions = async (signature: string) => {
        setAcceptButtonLoading(true);
        setTermsAndConditions(true);
        let payload = {
            signature_photo: signature,
        };

        let res = await PatchAPI(
            `${type?.toLowerCase()?.split(" ").join("-")}/${user_id}`,
            payload
        );
        if (!res.error) {
            setTermsAndConditions(false)
            navigate(`/user-management/${type
                ?.toLowerCase()
                .replace(/ /g, "-")
                .slice(0, -1)}-management`);
            dispatch(showToast({ message: "Details added successfully", type: "success" }));
            dispatch(clearSignaturePath());
        } else {
            dispatch(showToast({ message: res.message, type: "error" }));
        }
        setAcceptButtonLoading(false);
    };
    // Inputs for the dropdown only
    const dropdownInputs = {
        "": {
            [(parentMDSelected && type !== 'Master Distributors') ? "Parent Service Fee Type" : "Service Fee Type"]: {
                id: "service_fee_type",
                type: "dropDown",
                required: true,
                disabled: (parentMDSelected && type !== 'Master Distributors') ? true : false,
                noSearch: true,
                options: ["Upfront", "Postpaid"],
                tabIndex: 0,
            },
            ...(localFormState.service_fee_type === "Upfront" && {
                [(parentMDSelected && type !== 'Master Distributors') ? "Parent Service Fee Percentage" : "Specify the Service Fee Percentage"]: {
                    required: true,
                    disabled: (parentMDSelected && type !== 'Master Distributors') ? true : false,
                    id: "upfront_service_fee_percentage",
                    type: "numberInput",
                    maxLength: 60,
                    tabIndex: 0,
                    percentage: true,
                    placeholder: 'Enter percentage',
                    min: 0,
                    max: 100,
                    decimalPlaces: 2,
                    ...((!parentMDSelected) && { secondaryText: `Note : Percentage must not exceed ${maxUpfrontServiceFeePercent.toFixed(2)}%` })
                }
            }),
        }
    };
    const childDropdownInputs = {
        "": {
            ...(localFormState.service_fee_type === "Upfront" && {
                "Child Service Fee Percentage": {
                    required: true,
                    disabled: false,
                    id: "child_upfront_service_fee_percentage",
                    type: "numberInput",
                    maxLength: 60,
                    tabIndex: 0,
                    percentage: true,
                    placeholder: 'Enter percentage',
                    min: 0,
                    max: 100,
                    decimalPlaces: 2,
                    secondaryText: `Note : Percentage must not exceed ${localFormState.upfront_service_fee_percentage}%`
                }
            }),
        }
    };

    const handleChildUpfrontServiceFeePercentage = (id: string, value: string) => {
        setChildUpfrontFeePercentage(value);
        setChildUpfrontError({ child_upfront_service_fee_percentage: '' });
        // Store in Redux with child key
        if (localFormState.service_fee_type === "Upfront") {
            dispatch(setChildUpfrontServiceFee(Number(value)));
        }
    }

    const handleInputChange = (id: string, value: string) => {
        if (id === "service_fee_type" && value === "ZoodPay") {
            setParentMDSelected(false);
            handleSwitchToZoodPay();
        }
        if (id === "service_fee_type") {
            // Only clear details if switching between different types
            if (value !== localFormState.service_fee_type) {
                setLocalFormState(prev => ({
                    service_fee_type: value,
                    service_fee_parent: prev.service_fee_parent,
                    service_fee_parent_id: prev.service_fee_parent_id,
                    upfront_service_fee_percentage: '',
                    service_fee_details: value === "Postpaid" ? [{
                        mno_id: '',
                        mno_name: '',
                        bundle_dispersive_fee_percent: 0,
                        etopup_dispersive_fee_percent: 0,
                        status: true
                    }] : []
                }));

                setMaxUpfrontServiceFeePercent(0);
                setChildPostpaidPercentage({});
                setChildPostpaidErrors({});
                setChildUpfrontFeePercentage('');
                setChildUpfrontError({ child_upfront_service_fee_percentage: '' });
                setLocalErrors({});

                if (value === 'Upfront') {
                    handleMaxUprontPercentage();
                }
            } else {
                // If selecting the same type, just update the type
                setLocalFormState(prev => ({
                    ...prev,
                    service_fee_type: value
                }));
            }

            const newErrors = { ...localErrors };
            delete newErrors.service_fee_type;
            setLocalErrors(newErrors);
        } else if (id === "upfront_service_fee_percentage") {
            if (value === '' || value === '-' || value === '.' || value.endsWith('.') || value === '0') {
                setLocalFormState(prev => ({
                    ...prev,
                    upfront_service_fee_percentage: value
                }));
                const newErrors = { ...localErrors };
                delete newErrors.upfront_service_fee_percentage;
                setLocalErrors(newErrors);
                return;
            }

            if (!isNaN(Number(value))) {
                setLocalFormState(prev => ({
                    ...prev,
                    upfront_service_fee_percentage: value
                }));

                // Clear the error for this field if it exists
                const newErrors = { ...localErrors };
                delete newErrors.upfront_service_fee_percentage;
                setLocalErrors(newErrors);
            } else {
                setLocalErrors(prev => ({
                    ...prev,
                    upfront_service_fee_percentage: "Please enter a valid percentage"
                }));
            }
        }
        else if (id === "service_fee_parent") {
            // Handle parent selection
            setLocalFormState(prev => ({
                ...prev,
                service_fee_parent: value,
                ...(value !== "ZoodPay" ? {} : { service_fee_parent_id: "" })
            }));

            // Clear any errors related to parent
            const newErrors = { ...localErrors };
            delete newErrors.service_fee_parent;
            setLocalErrors(newErrors);
        }
        else if (id === "service_fee_parent_id") {
            // Handle parent ID selection
            const currentParent = localFormState.service_fee_parent;

            if (currentParent !== "ZoodPay") {
                setLocalFormState(prev => ({
                    ...prev,
                    service_fee_parent_id: value
                }));

                // Clear any errors related to parent ID
                const newErrors = { ...localErrors };
                delete newErrors.service_fee_parent_id;
                setLocalErrors(newErrors);
            }
        }
    };

    const handleSwitchToZoodPay = () => {
        // Reset parent MD selection flag
        setParentMDSelected(false);

        // Reset parent service fee related states
        setParentServiceFeeType("");
        setParentUpfrontServiceFee(0);
        setParentServiceFeeDetails([]);

        // Reset the form state to default values
        setLocalFormState({
            service_fee_type: "",
            service_fee_parent: "ZoodPay",
            service_fee_parent_id: "",
            upfront_service_fee_percentage: 0,
            service_fee_details: []
        });

        // Clear any errors
        setLocalErrors({});
    };

    const handleNestedDropdownSelect = (value: any, action: boolean) => {
        if (typeof value === 'string' && value === "ZoodPay") {
            handleSwitchToZoodPay();
            setParentMDSelected(false);
            setLocalFormState(prev => ({
                ...prev,
                service_fee_parent: value
            }));
        } else {
            handleMDSelection(value, !!action);
            handleMaxUprontPercentage();
        }
        // Clear all fields and states
        setMaxUpfrontServiceFeePercent(0);
        setChildPostpaidPercentage({});
        setChildPostpaidErrors({});
        setChildUpfrontFeePercentage('');
        setChildUpfrontError({ child_upfront_service_fee_percentage: '' });
        setLocalErrors({});
    };

    const handleIncreaseDecrease = (value: string) => {
        setSelectedOption(value);
    }

    const handleIncreaseDecreasePercentage = (id: string, value: number | string) => {
        // Convert to string and handle decimal places
        let strValue = String(value);

        // Handle empty input or just a decimal point
        if (strValue === '' || strValue === '.') {
            setSelectedOptionPercentage(0);
            return;
        }

        // Handle decimal input
        if (strValue.includes('.')) {
            const [whole, decimal] = strValue.split('.');
            // Limit decimal places to 2
            if (decimal && decimal.length > 2) {
                strValue = `${whole}.${decimal.slice(0, 2)}`;
            }
        }

        // Convert to number for validation
        const numericValue = Number(strValue);

        // Check if it's a valid number and doesn't exceed 100
        if (!isNaN(numericValue)) {
            if (numericValue <= 100) {
                setSelectedOptionPercentage(strValue);
            } else {
                // If exceeds 100, keep the whole number part if it's less than 100
                const wholeNumber = Math.floor(numericValue);
                if (wholeNumber <= 100) {
                    setSelectedOptionPercentage(wholeNumber);
                } else {
                    setSelectedOptionPercentage(100);
                }
            }
        }
    }

    const handleValidateAllFields = (isModalOpen?: boolean) => {
        const { errors, childErrors, hasChildErrors } = validateServiceFee();
        if (Object.keys(errors).length > 0 || hasChildErrors) {
            setLocalErrors(errors);
            setChildPostpaidErrors(childErrors);
            dispatch(setServiceFeeErrors(errors));
            return false;
        }
        if (parentMDSelected) {
            if (localFormState.service_fee_type === "Upfront") {
                if (Number(childUpfrontFeePercentage) > Number(localFormState.upfront_service_fee_percentage)) {
                    setChildUpfrontError({
                        child_upfront_service_fee_percentage: `Percentage must not exceed ${localFormState.upfront_service_fee_percentage}%`
                    })
                    return false;
                }
                if (childUpfrontFeePercentage === '') {
                    setChildUpfrontError({ child_upfront_service_fee_percentage: 'This field is required' });
                    return false;
                }
                if (Number(childUpfrontFeePercentage) < 0.01) {
                    setChildUpfrontError({ child_upfront_service_fee_percentage: 'Percentage must be greater than or equal to 0.01' });
                    return false;
                }
            }
        }

        // Only show edit modal if there are changes
        if (isModalOpen && hasChanges() && type !== 'Retailers') {
            setIsEditModal(true);
        } else if (isModalOpen) {
            // No changes detected, proceed without showing edit modal
            handleNext?.();
            setIsModalOpen(false);
            setIsEditModal(false);
            if (profile === 'Pending' && id !== '') {
                setTermsAndConditions(true);
            } else if (profile !== 'Pending' && id !== '') {
                setTermsAndConditions(false);
                navigate(`/user-management/${type
                    ?.toLowerCase()
                    .replace(/ /g, "-")
                    .slice(0, -1)}-management`);
                dispatch(showToast({ message: "No changes detected", type: "info" }));
            }
        }
        return true;
    }

    const hasChanges = () => {
        if (!service_fee) return true; // If no service fee in Redux, consider it as changed

        // Check service fee type
        if (localFormState.service_fee_type !== service_fee.service_fee_type) return true;

        // Check service fee parent
        if (localFormState.service_fee_parent !== service_fee.service_fee_parent) return true;

        // Check service fee parent ID
        if (localFormState.service_fee_parent_id !== service_fee.service_fee_parent_id) return true;

        if (localFormState.service_fee_type === "Upfront") {
            // Check upfront service fee percentage
            if (Number(localFormState.upfront_service_fee_percentage) !== Number(service_fee.upfront_service_fee_percentage)) return true;

            // Check child upfront service fee percentage if parent is selected
            if (parentMDSelected && Number(childUpfrontFeePercentage) !== Number(service_fee.child_upfront_service_fee_percentage)) return true;
        } else if (localFormState.service_fee_type === "Postpaid") {
            // Check if number of details changed
            if (localFormState.service_fee_details.length !== service_fee.service_fee_details.length) return true;

            // Check each detail
            for (let i = 0; i < localFormState.service_fee_details.length; i++) {
                const currentDetail = localFormState.service_fee_details[i];
                const reduxDetail = service_fee.service_fee_details[i];

                if (!reduxDetail) return true;

                // Check MNO ID
                if (currentDetail.mno_id !== reduxDetail.mno_id) return true;

                // Check bundle percentage
                if (Number(currentDetail.bundle_dispersive_fee_percent) !== Number(reduxDetail.bundle_dispersive_fee_percent)) return true;

                // Check etopup percentage
                if (Number(currentDetail.etopup_dispersive_fee_percent) !== Number(reduxDetail.etopup_dispersive_fee_percent)) return true;

                // Check status
                if (currentDetail.status !== reduxDetail.status) return true;

                // Check child percentages if parent is selected
                if (parentMDSelected) {
                    if (Number(currentDetail.child_bundle_dispersive_fee_percent) !== Number(reduxDetail.child_bundle_dispersive_fee_percent)) return true;
                    if (Number(currentDetail.child_etopup_dispersive_fee_percent) !== Number(reduxDetail.child_etopup_dispersive_fee_percent)) return true;
                }
            }
        }

        return false;
    };

    useImperativeHandle(ref, () => ({
        handleSubmit: () => {
            if (id !== '' && service_fee && Object.keys(service_fee).length > 0 && type !== 'Retailers') {
                if (hasChanges()) {
                    handleValidateAllFields(true);
                } else {
                    // No changes detected, just proceed without API call
                    handleNext();
                    setIsModalOpen(false);
                    setIsEditModal(false);
                    if (profile === 'Pending' && id !== '') {
                        setTermsAndConditions(true);
                    } else if (profile !== 'Pending' && id !== '') {
                        setEditLoading(true)
                    }
                }
            } else {
                // For new service fee or when no service fee exists
                dispatch(setAddLoading(true));
                handleSubmit();
            }
        },
    }));

    useEffect(() => {
        if (service_fee && (service_fee.service_fee_type === "Upfront")) {
            handleMaxUprontPercentage();
        }
        // eslint-disable-next-line
    }, []);

    // Add useEffect to notify parent of changes
    useEffect(() => {
        if (onHasChanges) {
            onHasChanges(hasChanges());
        }
    }, [localFormState, childUpfrontFeePercentage, childPostpaidPercentage, onHasChanges]);

    const [mnoList, setMnoList] = useState<MNOOption[]>([]);

    return (
        <div className="">
            <div className="flex gap-8 w-full">
                {isTemporaryUser && <AddInputs
                    inputs={{
                        "": {
                            "Enter the Service Fee Percentage": {
                                id: "user_role",
                                type: "dropDown",
                                disabled: false,
                                required: true,
                                tabIndex: 1,
                                placeholder: 'Select a user role',
                                noSearch: true,
                                options: ['Master Distributor', 'Distributor', 'Sub Distributor', 'Agent', 'Retailer'],
                            }
                        }
                    }}
                    totalInputCount={1}
                    inputValues={{
                        user_role: ''
                    }}
                    onInputChange={() => { }}
                    customWidth={true}
                    mapping={true}
                />}
                {type !== "Master Distributors" && (
                    <div className="mb-4 w-[48%]">
                        <InputLabel labelName={'Parent User'} id={user_id} required={true} />
                        <div className="flex flex-col">
                            <NestedDropdown
                                options={[
                                    { label: "ZoodPay", value: "ZoodPay" },
                                    ...(TYPE_MAPPINGS[type as keyof typeof TYPE_MAPPINGS]?.parentOptions || [])
                                        .filter(option => option !== "ZoodPay")
                                        .map(option => ({
                                            label: option,
                                            value: option,
                                            isNested: true,
                                            apiEndpoint: `${option.toLowerCase().split(" ").join("-")}`,
                                            id: option.toLowerCase().split(" ").join("-")
                                        }))
                                ]}
                                onSelect={handleNestedDropdownSelect}
                                selectedData={localFormState.service_fee_parent}
                                fetchUserData={fetchMasterDistributors}
                                testId="parent-user-dropdown"
                                errorMessage={localErrors.service_fee_parent}
                            />
                        </div>
                    </div>
                )}
                <AddInputs
                    inputs={dropdownInputs}
                    totalInputCount={
                        type !== "Master Distributors" && localFormState.service_fee_type === "Upfront"
                            ? 2
                            : type === "Master Distributors" && localFormState.service_fee_type === "Upfront"
                                ? 1
                                : 2
                    }
                    inputValues={{
                        ...(type !== "Master Distributors" && { service_fee_parent: localFormState.service_fee_parent }),
                        service_fee_type: localFormState.service_fee_type,
                        ...(localFormState.service_fee_type === "Upfront" && {
                            upfront_service_fee_percentage: localFormState.upfront_service_fee_percentage,
                        }),
                    }}
                    onInputChange={handleInputChange}
                    serviceFeeWidth={type !== "Master Distributors" ? true : false}
                    customWidth={true}
                    errors={localErrors}
                />
            </div>
            {(parentMDSelected && type !== 'Master Distributors') && <AddInputs
                inputs={childDropdownInputs}
                totalInputCount={1}
                inputValues={{
                    child_upfront_service_fee_percentage: childUpfrontFeePercentage,
                }}
                onInputChange={handleChildUpfrontServiceFeePercentage}
                mapping={true}
                errors={childUpfrontError}
            />}
            {localFormState.service_fee_type === "Postpaid" && (
                <PostpaidServiceFeeFields
                    service_fee_details={localFormState.service_fee_details}
                    parentMDSelected={parentMDSelected}
                    type={type}
                    onServiceFeeDetailsChange={(details) => {
                        setLocalFormState(prev => ({
                            ...prev,
                            service_fee_details: details
                        }));
                    }}
                    onErrorsChange={setLocalErrors}
                    localErrors={localErrors}
                    childPostpaidPercentage={childPostpaidPercentage}
                    onChildPostpaidPercentageChange={setChildPostpaidPercentage}
                    childPostpaidErrors={childPostpaidErrors}
                    onChildPostpaidErrorsChange={setChildPostpaidErrors}
                    onMnoListChange={setMnoList}
                />
            )}
            {isModalOpen && (
                <CustomModal
                    isOpen={isModalOpen}
                    onClose={() => {
                        setIsModalOpen(false);
                        setTermsAndConditions(true);
                    }}
                    title="Add CVM"
                    subText="This action will add additional service fee for the user."
                    // note="Note: This action cannot be undone."
                    primaryButtonText="Add"
                    secondaryButtonText="Cancel"
                    handelSubmit={() => {
                        setAddCvmClicked(true);
                    }}
                    maxWidth="max-w-[461px]"
                    buttonWidth="w-[85px]"
                    closeOnOverlayClick={false}
                />
            )}
            {isEditModal && (
                <CustomModal
                    isOpen={isEditModal}
                    onClose={() => {
                        setIsEditModal(false);
                        setSelectedOptionPercentage(0);
                        setSelectedOption("Decrease");
                    }}
                    title="Update Child Service Fee"
                    subText={
                        localFormState.service_fee_type !== service_fee?.service_fee_type
                            ? "The service fee type has been changed. All child users have had their service fee percentages reset to 0% and must be reconfigured as needed"
                            : localFormState.service_fee_type === "Upfront"
                                ? "Please enter the percentage increase or decrease you'd like to apply for the child users. This adjustment will apply to the upfront service fee percentage."
                                : `Please enter the percentage increase or decrease you'd like to apply for the child users. This adjustment will apply only to the following MNOs that were updated for the parent user: [${localFormState.service_fee_details
                                    .filter(detail => detail.status)
                                    .map(detail => mnoList.find(mno => mno.id === detail.mno_id)?.name)
                                    .filter(Boolean)
                                    .join(", ")}].`
                    }
                    primaryButtonText="Update"
                    secondaryButtonText="Cancel"
                    handelSubmit={() => {
                        handleSubmit();
                    }}
                    children={
                        localFormState.service_fee_type !== service_fee?.service_fee_type
                            ? null
                            : <>
                                <CustomRadioButton
                                    options={[
                                        { id: "Decrease", label: "Decrease" },
                                        { id: "Increase", label: "Increase" },
                                    ]}
                                    selected={selectedOption}
                                    onChange={handleIncreaseDecrease}
                                    padding="py-3 pr-3"
                                    extraClasses="mt-2"
                                />
                                <AddInputs
                                    inputs={{
                                        "": {
                                            "Service Fee Percentage": {
                                                id: "adjust_child_service_fee",
                                                type: "numberInput",
                                                disabled: false,
                                                required: true,
                                                maxLength: 60,
                                                tabIndex: 1,
                                                percentage: true,
                                                placeholder: 'Enter percentage',
                                                min: 0,
                                                max: 100,
                                                decimalPlaces: 2
                                            }
                                        }
                                    }}
                                    totalInputCount={1}
                                    inputValues={{
                                        adjust_child_service_fee: selectedOptionPercentage
                                    }}
                                    onInputChange={handleIncreaseDecreasePercentage}
                                // mapping={true}
                                />
                            </>
                    }
                    buttonLoading={editModalLoading}
                    maxWidth="max-w-[461px]"
                    buttonWidth="w-[85px]"
                />
            )}
            {termsAndConditions && (
                <TermsAndConditionsModal
                    isOpen={termsAndConditions}
                    onClose={() => {
                        setTermsAndConditions(false);
                        dispatch(clearSignaturePath());
                    }}
                    secondaryButtonText="Decline"
                    primaryButtonText="Accept"
                    maxWidth="max-w-[461px]"
                    buttonWidth="w-[85px]"
                    buttonLoading={acceptButtonLoading}
                    // primaryButtonClassName
                    handleSubmitButtonClick={handleTermsAndConditions}
                />
            )}
        </div>
    );
});

export default ServiceFee;
